/**
 * Левый список бэклога — docs/ui-design.md §5.3, макеты Main.html и Backlog.html.
 *
 * Данные приходят пропсами: владелец загрузки — WorkspacePage, потому что после каждого
 * решения меняется и панель справа, и строка слева, а один refetch на двоих гарантирует,
 * что список не останется с прежним статусом (см. шапку WorkspacePage.tsx).
 *
 * ПОДПИСЬ СТРОКИ — ЗАГОЛОВОК С СЕРВЕРА. До жалобы заказчика 26.08 («в бэклоге случайные
 * наборы букв») строка подписывалась `fixture_id ?? draft_id.slice(0, 8)`, то есть
 * идентификатором: заголовков тем не существовало до фазы 2. Теперь `title` считает бэкенд —
 * заголовок темы, а без темы первая строка канонического текста (`BacklogItemSchema`,
 * packages/contracts/src/api/ui.ts). Идентификатор черновика остался подсказкой при
 * наведении: он нужен, когда пост обсуждают в переписке, и не нужен, когда его выбирают
 * глазами.
 *
 * СВЁРНУТОЕ СОСТОЯНИЕ (правка заказчика 26.08). На экране черновика список занимает 380px
 * ради подписей, которые нужны при ВЫБОРЕ поста и не нужны при его ЧТЕНИИ. Кнопка в шапке
 * сворачивает панель в полосу кружков: пост остаётся достижим в один клик, а тексту
 * достаётся вся ширина. Кружок — не украшение: он несёт первую букву заголовка (отличить
 * соседей), цвет статуса каймой и фиолетовую заливку, если пост ЖДЁТ РЕШЕНИЯ. Наведение
 * даёт полный заголовок — то же, что видно в развёрнутом виде.
 *
 * ВНУТРИ КРУЖКА — НОМЕР, А НЕ БУКВА. Первая буква заголовка выглядела набором случайных
 * знаков («Д», «R», «A»): заголовки начинаются с чего угодно, и одна буква не отвечает ни на
 * «о чём это», ни на «который по счёту». Номер — сквозной номер поста (`post_no`), тот же, что
 * «post #N» в строке развёрнутого списка и в шапке черновика: один пост — один номер на всех
 * экранах. До 10.09.2026 кружок нёс позицию в списке, и она расходилась с номером поста.
 *
 * ПОМЕТКА «АВТО» (релиз 2026-10-14). Черновик, который заказало расписание бренда, а не
 * человек, несёт рядом со статусом контурную пометку `AutoBadge`; статус у него обычный
 * («ожидает»). В свёрнутой полосе пометка уходит словами в подсказку кружка.
 *
 * СЛЕД АВТООДОБРЕНИЯ (2026-10-25, docs/ai-lab.md). Текущая версия, которую одобрило или
 * пропустило автоодобрение бренда, несёт `AutoApprovalBadge` — «автоодобрен» / «не
 * автоодобрен», причина пропуска в подсказке. Другое слово, чем «авто»: то — кто пост заказал,
 * это — кто его одобрил. В свёрнутой полосе — тоже словами в подсказке кружка.
 *
 * ПОДПИСЬ СТРОКИ — НОМЕР И ВРЕМЯ. «сервис генерации · v1» убрано 10.09.2026: происхождение и
 * номер версии не помогают выбрать пост из списка, а версию экран черновика показывает сам,
 * когда их больше одной.
 *
 * ЖДЁТ РЕШЕНИЯ — ЭТО `pending_review`, И ТОЛЬКО ОН. `changes_requested` ждёт новой версии от
 * сервиса, а не человека; `failed`/`expired` требуют внимания, но это разбор аварии, а не
 * решение по тексту. Подсветить их тем же цветом значило бы позвать человека туда, где от
 * него сейчас ничего не зависит. Группа та же, что у чипа «Ожидают» в развёрнутом списке.
 *
 * СОСТОЯНИЕ ПЕРЕЖИВАЕТ ПЕРЕХОДЫ и хранится в localStorage. Без этого фильтр сбрасывался бы
 * на каждом клике по кружку — то есть ровно в том сценарии, ради которого свёрнутый вид и
 * заведён: свернул, читаю, перешёл к следующему.
 *
 * НА ТЕЛЕФОНЕ УМОЛЧАНИЕ — СВЁРНУТ (`narrow()`, docs/ui-design.md §4.1). Там список и пост
 * стоят одной колонкой, и развёрнутый список отдал бы посту второй экран вместо первого.
 *
 * ВРЕМЯ ПОКАЗЫВАЕТСЯ В ЗОНЕ ИНТЕРФЕЙСА И ПОДПИСАНО. До 08.09.2026 здесь стоял UTC, и причина
 * была настоящая: локальная минута бренда требует его часовой зоны, а зона приезжает только
 * в `GET /api/v1/brands` (09 §9.4) — в пропсах списка её нет. Изменилось не это, а решение:
 * один и тот же момент, подписанный на одном экране то в UTC, то в зоне бренда (слоты
 * бэкенд считает уже локально), путает сильнее, чем зашитая зона показа. Зона объявлена
 * ОДИН раз — `DISPLAY_TZ` в `components/format.ts`, — и это зона ПОКАЗА, а не публикации.
 * Единица («МСК») по-прежнему стоит у каждого времени, ровно как «кодпоинтов» у счётчика
 * длины: момент без подписи зоны врёт на три часа тому, кто помнит прежний вид.
 */

import { postLabel } from '@smm/contracts/schemas';
import { type JSX, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { ApiRequestError } from '../api/client.ts';
import type { BacklogItem } from '../api/types.ts';
import { agentsOffReason, useBrands } from '../brand.tsx';
import { AutoApprovalBadge, autoApprovalTitle } from '../components/AutoApprovalBadge.tsx';
import { AUTO_BADGE_TITLE, AutoBadge } from '../components/AutoBadge.tsx';
import { plural, shortMsk } from '../components/format.ts';
import { IconWarn } from '../components/icons.tsx';
import { StatusPill } from '../components/StatusPill.tsx';
import {
  ALL_GROUP_ID,
  STATUS_GROUPS,
  statusGroupId,
  statusLabel,
  statusPillClass,
} from '../components/status.ts';
import { styleMetaOf } from '../components/styleVersions.ts';
import styles from './BacklogAside.module.css';
import type { BacklogAsideProps } from './contracts.ts';

/** Ключ хранится один на приложение; список бэклога — единственная сворачиваемая панель. */
const COLLAPSED_KEY = 'smm.backlog.collapsed';

/**
 * Хранилище берётся У `window`, А НЕ У `globalThis` — и это не стилистика. В Node 26 есть
 * СВОЙ глобальный `localStorage`, недоступный без флага `--localstorage-file`; под vitest он
 * перекрывает хранилище jsdom, и обращение через `globalThis` молча возвращает `undefined`.
 * В браузере `globalThis === window`, поэтому разницы нет, а в тестах есть.
 *
 * Обёртки в try/catch, потому что доступ бросает в двух реальных случаях: приватный режим
 * Safari и запрет сторонних данных. Свёрнутость — удобство, а не состояние документа: падать
 * из-за неё экран не имеет права, поэтому отказ хранилища читается как «развёрнуто».
 */
function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readCollapsed(): boolean {
  try {
    return storage()?.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean): void {
  try {
    storage()?.setItem(COLLAPSED_KEY, v ? '1' : '0');
  } catch {
    /* см. storage(): молчим намеренно */
  }
}

/**
 * Узкий экран — телефон по шкале docs/ui-design.md §4.1. На ≤640 список и пост стоят В ОДНУ
 * КОЛОНКУ (`.body` в Shell.module.css), то есть развёрнутый список занимает первый экран
 * целиком и пост оказывается под ним, за прокруткой. Умолчание «свёрнут» отдаёт первый экран
 * посту, ради которого сюда и заходят, а список остаётся в один клик — ровно тот же
 * компромисс, ради которого свёрнутый вид заведён 26.08 на десктопе.
 *
 * Это УМОЛЧАНИЕ, а не запрет: кнопка разворачивает панель на любой ширине, и развёрнутой она
 * остаётся до конца работы с экраном. Сохранённый выбор при этом умеет только ДОБАВИТЬ
 * сворачивание (`readCollapsed()` первым в `||`), но не отменить его на телефоне — и это
 * сознательно: `smm.backlog.collapsed` один на все ширины, и «развернул на 27 дюймах» не
 * должно превращаться в «развёрнут и на 390px», где эта же панель отбирает первый экран.
 *
 * ЗАЩИТА `typeof window.matchMedia === 'function'` ОБЯЗАТЕЛЬНА, и не только ради jsdom
 * (который `matchMedia` не реализует — там ветка короткозамыкается, и умолчание остаётся
 * «развёрнут», как и было). То же самое верно для любой среды без раскладки: серверный
 * рендер, снимок страницы, тестовый прогон. Образец защиты — `HoldToConfirm.tsx`.
 *
 * `window.innerWidth` для этого НЕ ГОДИТСЯ: в jsdom он равен ровно 1024, то есть ответ
 * получился бы не «неизвестно», а случайно верным — и сломался бы от смены умолчания jsdom.
 * Порог здесь литералом по той же причине, что и во всех медиазапросах пакета: переменные
 * CSS в условии медиазапроса не работают.
 */
function narrow(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 640px)').matches
    );
  } catch {
    return false;
  }
}

/** Ждёт решения человека — ровно та же группа, что у чипа «Ожидают». */
const AWAITS_DECISION = 'pending';

/**
 * Схема обещает непустой `title`, но ответ бэкенда здесь не разбирается схемой, а
 * приводится типом (`useResource` в WorkspacePage). Пустая подпись — это пустая строка
 * списка, поэтому запасной вариант есть, и он словами, а не идентификатором: строка
 * «0199a000» не отвечает на вопрос «о чём это», ради которого заголовок и заведён.
 */
const titleOf = (item: BacklogItem): string => {
  const title = item.title.trim();
  return title === '' ? 'Без заголовка' : title;
};

/**
 * Мета строки: номер, время и — только когда пост отличается стилем — ярлык стиля
 * (docs/release-2026-09-19.md §5.4, §5.5): «стиль v6» у копии, «A · v5» / «B · v6» у пары
 * сравнения. У релиза ярлыка нет: подпись «стиль v5» у каждой строки была бы шумом ровно там,
 * где отличие должно бросаться в глаза.
 */
const metaOf = (item: BacklogItem): string => {
  const style = styleMetaOf(item.style, item.comparison);
  return [
    postLabel(item.post_no),
    item.slot_at === null
      ? `обновлён ${shortMsk(item.updated_at)}`
      : `слот ${shortMsk(item.slot_at)}`,
    ...(style === null ? [] : [style]),
  ].join(' · ');
};

function errorLine(error: Error): string {
  if (error instanceof ApiRequestError) {
    return error.status === 0
      ? `код: ${error.code}`
      : `код: ${error.code} · HTTP ${String(error.status)}`;
  }
  return `код: ${error.name}`;
}

export function BacklogAside({
  brand,
  items,
  loading,
  error,
  selectedDraftId,
}: BacklogAsideProps): JSX.Element {
  const generationOff = agentsOffReason(useBrands(), brand, ['generation']);
  const [group, setGroup] = useState<string>(ALL_GROUP_ID);
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed() || narrow());

  const list = useMemo(() => items ?? [], [items]);

  /**
   * Счётчики считаются по ЗАГРУЖЕННОМУ списку, а не запрашиваются у бэкенда. Причина не в
   * экономии запроса: сумма чипов обязана сходиться с числом строк, которые человек видит,
   * а серверный счётчик по другому срезу разойдётся с ним при первом же расхождении фильтров.
   */
  const counts = useMemo(() => {
    const acc = new Map<string, number>();
    for (const item of list) {
      const id = statusGroupId(item.status);
      acc.set(id, (acc.get(id) ?? 0) + 1);
    }
    return acc;
  }, [list]);

  const visible = useMemo(
    () => (group === ALL_GROUP_ID ? list : list.filter((i) => statusGroupId(i.status) === group)),
    [list, group],
  );

  const headerMeta =
    error !== undefined
      ? 'не загрузился'
      : items === undefined
        ? 'загрузка…'
        : `${String(list.length)} ${plural(list.length, 'черновик', 'черновика', 'черновиков')}`;

  const chips =
    error !== undefined || list.length === 0 ? null : (
      <div className={styles['filters']}>
        {[
          { id: ALL_GROUP_ID, label: 'Все', count: list.length },
          ...STATUS_GROUPS.map((g) => ({ id: g.id, label: g.label, count: counts.get(g.id) ?? 0 })),
        ]
          // Группы без единого черновика не показываются: пустой чип «Архив 0» не отвечает
          // ни на один вопрос и вытесняет из полосы те, по которым правда есть работа.
          .filter((c) => c.count > 0 || c.id === ALL_GROUP_ID)
          .map((c) => (
            <button
              key={c.id}
              type="button"
              className={c.id === group ? `fchip on ${styles['chip']}` : `fchip ${styles['chip']}`}
              aria-pressed={c.id === group}
              onClick={() => {
                setGroup(c.id);
              }}
            >
              {c.label} <span className="n">{c.count}</span>
            </button>
          ))}
      </div>
    );

  let body: JSX.Element;
  if (error !== undefined) {
    body = (
      <div className={styles['state']}>
        <div className="val val-err" role="alert">
          <IconWarn />
          <span>
            Бэклог не загрузился. {error.message}
            <br />
            <span className="meta">{errorLine(error)}</span>
          </span>
        </div>
        <span className="micro">
          Список не обновится сам: повторите действие или перезагрузите страницу.
        </span>
      </div>
    );
  } else if (items === undefined) {
    body = (
      <div className={styles['state']}>
        <span className="micro">{loading ? 'Загружаем бэклог…' : 'Бэклог не запрошен.'}</span>
      </div>
    );
  } else if (list.length === 0) {
    body = (
      <div className={styles['state']}>
        <span className="h3">Черновиков пока нет</span>
        {/* Пустой экран отвечает на «что делать», а не на «почему пусто»: кнопка, о которой
            здесь сказано, стоит в шапке и видна прямо сейчас. У бренда без генерации кнопка
            погашена, и звать к ней нельзя: черновики такому бренду пишет только сервис
            генерации, руками пост не заводится (docs/multi-brand.md §8). */}
        {generationOff === null ? (
          <span className="micro">
            Нажмите «Запустить генерацию» в шапке — сначала мониторинг обойдёт источники и подберёт
            тему (обычно 1–3 минуты), затем сервис генерации напишет черновик. Ход виден там же, в
            шапке, а готовый черновик появится здесь.
          </span>
        ) : (
          <span className="micro">
            {`${generationOff[0]?.toUpperCase() ?? ''}${generationOff.slice(1)}. Черновики этого бренда пишет только сервис генерации — они появятся здесь, когда его подключат.`}
          </span>
        )}
      </div>
    );
  } else if (visible.length === 0) {
    body = (
      <div className={styles['state']}>
        <span className="micro">В этом фильтре черновиков нет.</span>
        <button
          type="button"
          className={styles['linkBtn']}
          onClick={() => {
            setGroup(ALL_GROUP_ID);
          }}
        >
          Показать все
        </button>
      </div>
    );
  } else {
    body = (
      <div className={styles['list']}>
        <div className="rows">
          {visible.map((item) => {
            const active = item.draft_id === selectedDraftId;
            return (
              <Link
                key={item.draft_id}
                href={`/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(item.draft_id)}`}
                className={active ? `row on ${styles['rowLink']}` : `row ${styles['rowLink']}`}
                aria-current={active ? 'page' : undefined}
                title={item.draft_id}
              >
                <div className="row-t">
                  <StatusPill status={item.status} />
                  {item.auto ? <AutoBadge /> : null}
                  {item.auto_approval === undefined ? null : (
                    <AutoApprovalBadge approval={item.auto_approval} />
                  )}
                  <span className="row-title">{titleOf(item)}</span>
                </div>
                <div className="meta">{metaOf(item)}</div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  const toggle = (
    <button
      type="button"
      className={styles['toggle']}
      aria-expanded={!collapsed}
      aria-controls="backlog-body"
      title={collapsed ? 'Развернуть список' : 'Свернуть список'}
      onClick={() => {
        setCollapsed((v) => {
          writeCollapsed(!v);
          return !v;
        });
      }}
    >
      {/* Шеврон смотрит туда, куда поедет панель: влево — свернётся, вправо — раскроется. */}
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <path
          d={collapsed ? 'M6 3l5 5-5 5' : 'M10 3L5 8l5 5'}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  if (collapsed) {
    return (
      <aside className={styles['railAside']}>
        <div className={styles['railHead']}>{toggle}</div>
        <div className={styles['rail']} id="backlog-body">
          {visible.map((item) => {
            const active = item.draft_id === selectedDraftId;
            const awaits = statusGroupId(item.status) === AWAITS_DECISION;
            // «авто» — словами в подсказке: кружку второй значок не по размеру, а происхождение
            // поста при выборе из полосы так же полезно, как в развёрнутом списке.
            const said = `${postLabel(item.post_no)}. ${titleOf(item)} — ${statusLabel(item.status)}${item.auto ? ` · ${AUTO_BADGE_TITLE}` : ''}${item.auto_approval === undefined ? '' : ` · ${autoApprovalTitle(item.auto_approval)}`}`;
            const cls = [
              styles['dot'],
              awaits ? styles['dotAwaits'] : '',
              active ? styles['dotOn'] : '',
            ]
              .filter((c) => c !== '')
              .join(' ');
            return (
              <Link
                key={item.draft_id}
                href={`/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(item.draft_id)}`}
                className={cls}
                aria-current={active ? 'page' : undefined}
                /* Подсказка повторяет то, что видно в развёрнутом виде: заголовок и статус.
                   Без неё кружки различимы только по букве, а этого мало. */
                title={said}
              >
                <span aria-hidden="true">{item.post_no}</span>
                <span className={styles['srOnly']}>{said}</span>
                <i className={`${styles['dotState']} ${statusPillClass(item.status)}`} />
              </Link>
            );
          })}
        </div>
      </aside>
    );
  }

  return (
    <aside className="aside">
      <div className="aside-h">
        <span className="h3">Бэклог</span>
        <span className="meta">{headerMeta}</span>
        {toggle}
      </div>
      {chips}
      <div id="backlog-body" className={styles['bodyWrap']}>
        {body}
      </div>
    </aside>
  );
}
